import { Button, TableCell, TableHead, TableRow, Tooltip } from "@mui/material";

import Table, {
  StyledTableCell,
  StyledTableRow,
} from "../../../../Component/UI/Table/Table";
import MUICard from "../../../../Component/UI/MUICard/MUICard";
import classes from "./CategoryDims.module.css";
import { extractDomain } from "../../../../utils/helpers";
import {
  compareScores,
  sourceSplitToDomain,
} from "../CoreAttributes/CoreAttributes";
import { faSortUp, faSortDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import EmptyView from "../../../../Component/EmptyView/EmptyView";
import Spinner from "../../../../Component/Spinner/Spinner";
import AIGenerateButton from "../../../../Component/AIGenerateButton/AIGenerateButton";
import { metricExplainations } from "../../../../utils/constant";
import MUIToolTip from "../../../../Component/UI/ActionTooltip/MUIToolTip";
import {
  createActionMetric,
  fetchActionMetrics,
} from "../../../../redux/actions/metricActions";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { useState } from "react";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getSourcesWithAlias } from "../../../../services/apiService";
import { createDomainUrlMapping } from "../ActionComponent/ActionModal";
import DetailedTableModal from "../../common/MetricDetails";
import MUIIconButton from "../../../../Component/UI/MUIIconButton/MUIIconButton";
import { InfoOutlined } from "@mui/icons-material";
import { PaginatedCategoryDimensionsTable } from "./PaginatedCategoryDimensionsTable";
import Modal from "../../../../Component/Modal/Modal";

const TABLE_HEADERS = [
  {
    id: "attr",
    name: "Applicant Criteria",
  },
];

export default function CategoryDimensions({
  purchaseDimData,
  pastPurchaseDimData,
  brandDomains,
  loading,
  errors,
  summaries,
  getAttributesSummary,
  selectedInput,
  sources,
  customerId,
  sourceURLIds,
  fetchRealTimeActionsSources,
  isChecked
}) {
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Inside the CategoryDimensions component, add this state after existing useState calls:
  const [showReportingModal, setShowReportingModal] = useState(false);

  // Add this function to check reporting mode (add after existing functions):
  function isReportingMode() {
    const isReporting =
      new URLSearchParams(window.location.search).get("isReporting") === "true" ||
      new URLSearchParams(window.location.search).get("reporting") === "true";
    return isReporting;
  }

  function formatName(str) {
    return str
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  const saveActions = async (title, rationale, realSource) => {
    setIsActionLoading(true);
    realSource = sourceSplitToDomain(realSource);
    const actionSources = Object.entries(Object.values(sources || {})?.[0] || {})?.map(
      ([domain_url, { aliases = [] }]) => ({
        domain_url,
        fullUrl: Array.from(new Set(aliases.map((item) => item?.source_alias_url)))?.filter(Boolean),
      })
    );
    const finalSource =
      realSource && realSource.length > 0
        ? realSource
        : actionSources

    const modifiedObj = {
      title: title,
      rationale: rationale,
      brand: selectedInput?.brand || selectedInput?.product,
      persona: selectedInput?.persona,
      geography: selectedInput?.geography,
      market: selectedInput?.market,
      category: selectedInput?.category,
      llm: selectedInput?.llm,
      isProduct: selectedInput?.product ? true : false,
      sources: finalSource,
    };

    const payload = {
      action_json: [modifiedObj],
      is_archived: 0,
      is_active: 1,
    };

    dispatch(createActionMetric(customerId, payload))
      .then(() => {
        dispatch(fetchActionMetrics(customerId));
        setIsActionLoading(false);
        // navigate("/luminaire/actions?actions_list=1");
        toast.success("Action Added!", {
          autoClose: 700,
        });
      })
      .catch(() => {
        setIsActionLoading(false);
        toast.error("Something went wrong!", {
          autoClose: 700,
        });
      });
  };

  function extractUniqueBrands(data) {
    if (data && data.length) {
      // Find the attribute with the maximum number of competitors (brands)
      let maxCompetitorAttribute = data.reduce((maxAttr, currentAttr) => {
        return currentAttr.scores.length > maxAttr.scores.length
          ? currentAttr
          : maxAttr;
      });

      // Get the unique brand names from that attribute
      const uniqueBrands = new Set();
      maxCompetitorAttribute.scores.forEach((brandObj) => {
        uniqueBrands.add(brandObj.name);
      });

      // Create an array of objects with unique id and brand name
      const brandArray = Array.from(uniqueBrands).map((brandName, index) => ({
        id: index + 1, // Unique ID starting from 1
        name: brandName,
      }));

      return brandArray;
    }
  }

  function renderRows(mainItem, index) {
    const allBrands = extractUniqueBrands(purchaseDimData); // from headers

    return (
      <StyledTableRow key={index}>
        <StyledTableCell className={classes.attribute_headings}>
          {formatName(mainItem.name)}
        </StyledTableCell>

        {/* Loop through each brand in header order */}
        {allBrands.map((brandObj, brandIndex) => {
          // find matching score for this brand
          const scoreItem = mainItem.scores.find(
            (s) => s.name?.toLowerCase() === brandObj.name?.toLowerCase()
          );

          // fallback if not found
          if (!scoreItem || !scoreItem.score) {
            return (
              <StyledTableCell
                key={brandIndex}
                className={classes.attribute_score}
                style={{ color: "grey", opacity: 0.6 }}
              >
                NA
              </StyledTableCell>
            );
          }

          // determine color
          let color = "#dc3545";
          if (scoreItem.score >= 7) color = "#2196F3";
          if (scoreItem.score >= 9) color = "#198754";

          const isCustomTooltip =
            scoreItem?.name === (selectedInput?.brand || selectedInput?.product) &&
            selectedInput?.llm?.toLowerCase().includes("search");

          const TooltipComponent = isCustomTooltip ? MUIToolTip : Tooltip;

          const tooltipProps = isCustomTooltip
            ? {
              sourceURLIds,
              metric: "Applicant Criteria",
              realtimeSources: fetchRealTimeActionsSources,
              attribute: formatName(mainItem.name),
              metricScore: Number(scoreItem.score).toFixed(0),
              selectedInput,
              sources,
              key: brandIndex,
              addToActionHandler: saveActions,
              actionText: "Add To Action",
              title: scoreItem.explanation,
              isActionLoading,
              arrow: true,
              isBrandOverview: true,
              slotProps: {
                popper: {
                  modifiers: [
                    { name: "offset", options: { offset: [0, -18] } },
                  ],
                },
              },
            }
            : {
              title: scoreItem.explanation,
              arrow: true,
              slotProps: {
                popper: {
                  modifiers: [
                    { name: "offset", options: { offset: [0, -18] } },
                  ],
                },
              },
            };

          return (
            <TooltipComponent {...tooltipProps}>
              <StyledTableCell
                key={brandIndex}
                style={{ color }}
                className={classes.attribute_score}
              >
                {Number(scoreItem.score).toFixed(0)}
                &nbsp;&nbsp;
                {isChecked && scoreItem.delta !== null && (
                  <span style={{ color: "grey" }}>
                    <FontAwesomeIcon
                      className={
                        scoreItem.delta < 0
                          ? `${classes["core-down-icon"]} text-danger me-1`
                          : `${classes["core-up-icon"]} text-success me-1`
                      }
                      icon={scoreItem.delta < 0 ? faSortDown : faSortUp}
                    />
                    <span className={classes.coreDelta}>
                      {Number(Math.abs(scoreItem.delta)).toFixed(0)}
                    </span>
                  </span>
                )}
              </StyledTableCell>
            </TooltipComponent>
          );
        })}
      </StyledTableRow>
    );
  }


  function renderHeaders() {
    const headers = [...TABLE_HEADERS, ...extractUniqueBrands(purchaseDimData)];

    return (
      <TableHead className={classes.table_head}>
        <TableRow>
          {headers.map((header, index) => {
            let domain = extractDomain(brandDomains?.[header?.name]);

            return (
              <TableCell key={index} className={classes.table_head_cell}>
                <div className={classes.table_head_content}>
                  {index != 0 && (
                    <img
                      data-html2canvas-ignore
                      className={classes.table_head_logo}
                      src={`https://img.logo.dev/${domain}?token=pk_SaDf4t2eRsW5Fc84zgJRZA`}
                    />
                  )}
                  <div
                    className={
                      index === 0
                        ? classes.table_head_text_comp
                        : classes.table_head_text
                    }
                  >
                    <div className={classes.table_head_title}>
                      <span>
                        <Tooltip title={metricExplainations[header.name]}>
                          {header.name}
                        </Tooltip>
                      </span>
                      {index === 0 && !showModal && (
                        <MUIIconButton onClick={() => setShowModal(true)}>
                          <InfoOutlined />
                        </MUIIconButton>
                      )}
                    </div>
                  </div>
                </div>
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>
    );
  }

  const purchaseDimDataWithDelta = compareScores(
    purchaseDimData,
    pastPurchaseDimData
  )?.slice(0, 8);

  return (
    <>
      <MUICard variant="elevated" elevation={0} className={classes.card}>
        {purchaseDimDataWithDelta && purchaseDimDataWithDelta.length > 0 ? (
          <>

            {isReportingMode() && (
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "12px" }}>
                <Button
                  variant="outlined"
                  size="small"
                  // startIcon={<DescriptionIcon />}
                  onClick={() => setShowReportingModal(true)}
                  style={{ textTransform: "none" }}
                >
                  Applicant Criteria Report View
                </Button>
              </div>
            )}

            <Table
              elevation={0}
              data={purchaseDimDataWithDelta}
              renderHeaders={() => renderHeaders(purchaseDimData)}
              render={renderRows}
            />
            <DetailedTableModal
              show={showModal}
              onClose={() => setShowModal(false)}
              headers={extractUniqueBrands(purchaseDimData)?.map((item) => item?.name)}
              data={purchaseDimDataWithDelta}
              renderHeaders={renderHeaders}
              formatName={formatName}
              classes={classes}
              pdfHeight={300}
              pdfTitle={`${selectedInput?.brand || selectedInput?.product
                }-Applicant Criteria`}
            />

            <Modal
              handleClose={() => setShowReportingModal(false)}
              open={showReportingModal}
              title={`Applicant Criteria - Report View (Paginated for PPT)`}
              width={1200}
              sx={{
                maxWidth: "90vw",
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
              }}
            >
              <div style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%"
              }}>
                <div style={{
                  maxHeight: "70vh",
                  overflowY: "auto",
                  padding: "8px",
                  width: "100%"
                }}>
                  <PaginatedCategoryDimensionsTable
                    data={purchaseDimDataWithDelta}
                    uniqueBrands={extractUniqueBrands(purchaseDimData)}
                    brandDomains={brandDomains}
                    styleClasses={classes}
                    formatName={formatName}
                    isChecked={isChecked}
                    maxBrandsPerTable={6}
                  />
                </div>
              </div>
            </Modal>

          </>
        ) : (
          <>
            <h5>
              <Tooltip title={metricExplainations[`Purchase Criteria`]}>
                Applicant Criteria
              </Tooltip>
            </h5>
            <EmptyView message="Data Not Available" />
          </>
        )}
      </MUICard>

      <div className="col-md-12">
        <div className="graphGreyBg">
          <p className="mb-3 font14-600">
            Summary &nbsp; &nbsp;
            <AIGenerateButton
              fetchSummary={getAttributesSummary}
              type="purchase-criteria"
            />
          </p>
          <div className="graphsummary font-11">
            {loading?.purchaseCriteria ? (
              <Spinner />
            ) : summaries?.purchaseCriteria ? (
              <p className="mb-1"> {summaries?.purchaseCriteria} </p>
            ) : errors?.purchaseCriteria ? (
              <p style={{ color: "red", textAlign: "center" }}>
                {errors?.purchaseCriteria}
              </p>
            ) : null}
          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  );
}
